/**
 * Interface representing one impression (product) that is visible on page
 */
interface IEnhancedImpression {
    brand: string;
    category: string;
    id: string;
    list: string;
    name: string;
    position: number;
    price: number;
    variant: string;
}

/**
 * Interface representing one product (on detail page or in basket)
 */
interface IEnhancedProduct {
    brand: string;
    category: string;
    id: string;
    name: string;
    position: number;
    price: number;
    quantity: number;
    variant: string;
}

/**
 * Interface representing one promo action (banner)
 */
interface IEnhancedPromo {
    id: string;
    name: string;
    creative: string;
    position: string;
}

/**
 * Data layer object for Google Analytics
 */
declare var dataLayer: any;

/**
 * Měna
 */
declare var currency: string;

/**
 * Object containing impressions that can be seen on current page
 */
declare var enhancedImpressions: {
    impressions: IEnhancedImpression[];
};

/**
 * Object containing promos that can be seen on current page
 */
declare var enhancedPromos: {
    promos: IEnhancedPromo[];
};

/**
 * Object containing products that can be seen on current checkout page
 */
declare var enhancedProducts: {
    products: IEnhancedProduct[];
}

/**
 * Objekt containing information about product on detail page
 */
declare var enhancedProduct: IEnhancedProduct;

/**
 * SiteConfig ID
 */
declare var site: number;

/**
 * ID jazyka
 */
declare var langId: number;

/**
 * Kliknutí na produkt => odešle informaci o produktu do GA
 * @param code Kód produktu
 * @param list Typ listu (typ přehledu zboží)
 * @param url Url na produkt
 */
function productClick(code: string, list: string, url: string): void {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        window.location.href = url;
        return;
    }

    const retVal = findImpression(code, list);

    if (retVal === null || typeof retVal === "undefined") {
        window.location.href = url;
        return;
    }
    //console.log({
    dataLayer.push({
        "event": "productClick",
        "ecommerce": {
            "click": {
                "actionField": {
                    "list": list
                },
                "products": [{
                    "id": retVal.id,
                    "name": retVal.name,
                    "price": retVal.price,
                    "brand": retVal.brand,
                    "category": retVal.category,
                    "position": retVal.position
                }]
            }
        },
        'eventCallback': () => {
            window.location.href = url;
        },
        'eventTimeout': 2000
    });
}

/**
 * Kliknutí na banner => odešle informaci o banneru do GA
 * @param promoCode kód banneru
* @param url Url na produkt
*/
function promoClick(promoCode: string, url: string, target: string): void {
    let numberOfCallBacks = 0;
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        if (typeof target !== "undefined" && target === "_blank") {
            window.open(url, target);
        }
        else {
            window.location.href = url;
        }    
        
        return;
    }

    const promo = findPromotion(promoCode);

    if (promo === null || typeof promo === "undefined") {
        if (typeof target !== "undefined" && target === "_blank") {
            window.open(url, target);
        }
        else {
            window.location.href = url;
        }
        
        return;
    }

    //console.log({
    dataLayer.push({
        "event": "promotionClick",
        "ecommerce": {
            "promoClick": {
                "promotions": [{
                    "id": promo.id,
                    "name": promo.name,
                    "creative": promo.creative,
                    "position": promo.position
                }]
            }
        }
        ,
        'eventCallback': () => {
            if (typeof target !== "undefined" && target === "_blank" && numberOfCallBacks === 0) {
                window.open(url, target);
            }
            else if (numberOfCallBacks === 0) {
                window.location.href = url;
            }
            numberOfCallBacks++;
        },
        'eventTimeout': 2000
    });
}

function pushEvent(event) {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        return true;
    }

    if (typeof dataLayer === "undefined" || !Array.isArray(dataLayer)) {
        console.log("dataLayer is not defined");
        return true;
    }

    //console.log({
    dataLayer.push({
        "event": event
    });
}

/**
 * Kliknutí na nákup produktu z detailu
 * @param productParams parametry produktu
 */
function productBuyDetail(productParams: string): void {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        return;
    }

    productParams = `&${productParams}`;
    const code = GetParameterByName("code", productParams);
    const count = parseInt(GetParameterByName("count", productParams));

    if (typeof enhancedProduct === "undefined" || enhancedProduct === null) {
        return;
    }

    if (code !== enhancedProduct.id) {
        console.log("Neplatný kód.");
        return;
    }
    
    //console.log({
    dataLayer.push({
        "event": "addToCart",
        "ecommerce": {
            "currencyCode": currency,
            "add": {
                "products": [{
                    "id": code,
                    "name": enhancedProduct.name,
                    "price": enhancedProduct.price,
                    "brand": enhancedProduct.brand,
                    "category": enhancedProduct.category,
                    "variant": enhancedProduct.variant,
                    "position": 0,
                    "quantity": count
                }]
            }
        }
    });
}

/**
 * Kliknutí na nákup z katalogu (univerzálního)
 * @param productParams parametry produktu
 * @param list seznam, ve kterém jsem kliknul
 */
function productBuyCatalog(productParams: string, list: string): void {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        return;
    }

    productParams = `&${productParams}`;
    const code = GetParameterByName("code", productParams);
    const count = parseInt(GetParameterByName("count", productParams));
    const retVal = findImpression(code, list);

    if (retVal === null || typeof retVal === "undefined") {
        return;
    }

    //console.log({
    dataLayer.push({
        "event": "addToCart",
        "ecommerce": {
            "currencyCode": currency,
            "add": {
                "products": [{
                    "id": code,
                    "name": retVal.name,
                    "price": retVal.price,
                    "brand": retVal.brand,
                    "category": retVal.category,
                    "variant": retVal.variant,
                    "position": retVal.position,
                    "quantity": count
                }]
            }
        }
    });
}

/**
 * Změna množství v košíku
 * @param code kód produktu
 * @param oldCount původní množství v košíku
 * @param newCount nové množství v košíku
 * @param callback callback function
 */
function basketUpdateProduct(code: string, oldCount: number, newCount: number, callback: any): void {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        callback();
        return;
    }

    const isAdd = newCount > oldCount;

    if (newCount === 0 || (oldCount + newCount) === 0) {
        basketRemoveProduct(code, callback);
    }
    
    const prod = findBasketProduct(code);

    if (prod === null || typeof prod === "undefined") {
        callback();
        return;
    }
    
    if (Math.abs(newCount - oldCount) === 0) {
        callback();
        return;
    }
    console.log(1);
    if (isAdd) {
        //console.log({
        dataLayer.push({
            "event": "addToCart",
            "ecommerce": {
                "currencyCode": currency,
                "add": {
                    "products": [
                        {
                            "id": prod.id,
                            "name": prod.name,
                            "price": prod.price,
                            "brand": prod.brand,
                            "category": prod.category,
                            "variant": prod.variant,
                            "position": prod.position,
                            "quantity": Math.abs(newCount - oldCount)
                        }
                    ]
                }
            },
            'eventCallback': () => {
                console.log("posláno");
                callback();
                return;
            },
            'eventTimeout': 2000
        });
    } else {
        //console.log({
        dataLayer.push({
            "event": "removeFromCart",
            "ecommerce": {
                "currencyCode": currency,
                "remove": {
                    "products": [
                        {
                            "id": prod.id,
                            "name": prod.name,
                            "price": prod.price,
                            "brand": prod.brand,
                            "category": prod.category,
                            "variant": prod.variant,
                            "position": prod.position,
                            "quantity": Math.abs(newCount - oldCount)
                        }
                    ]
                }
            },
            'eventCallback': () => {
                console.log("posláno");
                callback();
                return;
            },
            'eventTimeout': 2000
        });
    }
}

/**
 * Odebrání produktu z košíku
 * @param code kód produktu
 * @param callback callback function
 */
function basketRemoveProduct(code: string, callback: any): void {
    if (!features.GoogleAnalyticsEnhancedEcommerce) {
        callback();
        return;
    }

    const prod = findBasketProduct(code);

    if (prod === null || typeof prod === "undefined") {
        callback();
        return;
    }

    if (prod.quantity === 0) {
        callback();
        return;
    }

    //console.log({
    dataLayer.push({
        "event": "removeFromCart",
        "ecommerce": {
            "currencyCode": currency,
            "remove": {
                "products": [{
                    "id": prod.id,
                    "name": prod.name,
                    "price": prod.price,
                    "brand": prod.brand,
                    "category": prod.category,
                    "variant": prod.variant,
                    "position": prod.position,
                    "quantity": prod.quantity
                }]
            }
        },
        'eventCallback': () => {
            callback();
            return;
        },
        'eventTimeout': 2000
    });
}

/**
 * Nalezení jedné impresse ze seznamu impressí
 * @param code kód hledaného produktu
 * @param list seznam, ve kterém se má hledat
 */
function findImpression(code: string, list: string): IEnhancedImpression {
    let retVal: IEnhancedImpression = null;
    if (typeof enhancedImpressions === "undefined") {
        return null;
    }

    const imp = enhancedImpressions.impressions;
    let i = 0;

    while (typeof (imp[i]) != "undefined") {
        if (imp[i].id === code && imp[i].list === list) {
            retVal = imp[i];
            break;
        }
        i++;
    }

    if (retVal === null || typeof retVal === "undefined") {
        return null;
    }

    return retVal;
}

/**
 * Nalezení jednoho proma ze seznamu promotions
 * @param code kód promotion
 */
function findPromotion(code: string): IEnhancedPromo {
    let retVal: IEnhancedPromo = null;

    if (typeof enhancedPromos === "undefined") {
        return null;
    }

    const promo = enhancedPromos.promos;
    let i = 0;

    while (typeof promo[i] != "undefined") {
        if (promo[i].id === code) {
            retVal = promo[i];
            break;
        }
        i++;
    }

    if (retVal === null || typeof retVal === "undefined") {
        return null;
    }

    return retVal;
}

/**
 * Nalezení jednoho produktu ze seznamu produktů
 * @param code kód produktu
 */
function findBasketProduct(code: string): IEnhancedProduct {
    let retVal: IEnhancedProduct = null;

    if (typeof enhancedProducts === "undefined") {
        return null;
    }

    const prod = enhancedProducts.products;
    let i = 0;

    while (typeof (prod[i]) != "undefined") {
        if (prod[i].id === code) {
            retVal = prod[i];
            break;
        }
        i++;
    }

    if (retVal === null || typeof retVal === "undefined") {
        return null;
    }

    return retVal;
}

function enhancedEcommerceEvent() {
    $("[data-ga-action]")        
        .on("click", function (e) {
            if (!features.GoogleAnalyticsEnhancedEcommerce) {
                return true;
            }

            const action = $(this).data("ga-action");
            let productCode = "";
            let $el = null;

            switch (action) {
                case "productClick":
                    e.preventDefault();
                    $el = $(this);
                    productCode = $el.data("ga-code").toString();
                    const prodUrl: string = $el.attr("href");
                    const list: string = $el.data("ga-list");

                    if (productCode === null ||
                        list === null ||
                        typeof (productCode) === "undefined" ||
                        typeof (list) === "undefined") {
                        return false;
                    }

                    productClick(productCode, list, prodUrl);
                    break;
                case "promotionClick":
                    e.preventDefault();
                    $el = $(this);
                    const promoCode: string = $el.data("ga-code").toString();
                    const promoUrl: string = $el.attr("href");
                    const promoTarget: string = $el.attr("target");

                    if (promoCode === null || typeof promoCode === "undefined") {
                        return false;
                    }

                    promoClick(promoCode, promoUrl, promoTarget);
                    break;
                //case "basketRemove":
                //    $el = $(this);
                //    const basketRemoveParam = $el.parent("form").serialize();
                //    const validation: string = $el.data("ga-confirm").toString();
                //    return basketRemoveProduct(basketRemoveParam, validation);
                //case "basketUpdate":
                //    $el = $(this);
                //    const basketUpdateParam = $el.parent("form").serialize();
                //    return basketUpdateProduct(basketUpdateParam);
                default:
                    return true;
            }

            return true;
        });
}

$(document).ready(() => {
    enhancedEcommerceEvent();
    });